/**
 * Client-side UA parsing for session analytics.
 * Device type reflects hardware class (phone/tablet/computer), not browser window width.
 */

/** iPadOS 13+ Safari may report as Mac — touch heuristic catches many tablets. */
export function isTabletLike(ua = navigator.userAgent) {
  const agent = String(ua);
  if (/ipad|tablet|playbook|silk/i.test(agent)) return true;
  if (/android/i.test(agent) && !/mobile/i.test(agent)) return true;
  if (typeof navigator !== 'undefined') {
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  }
  return false;
}

export function isMobilePhone(ua = navigator.userAgent) {
  const agent = String(ua);
  if (isTabletLike(agent)) return false;
  return /mobile|iphone|ipod|android|blackberry|iemobile|opera mini|windows phone/i.test(agent);
}

/**
 * Device category for analytics: desktop | mobile | tablet | unknown
 * Based on User-Agent (+ iPad touch heuristic), NOT viewport width.
 */
export function detectDeviceFromUserAgent(ua = navigator.userAgent) {
  const agent = String(ua);
  if (!agent) return 'unknown';
  if (isTabletLike(agent)) return 'tablet';
  if (isMobilePhone(agent)) return 'mobile';
  return 'desktop';
}

/** Optional: how wide the browser window is (layout), separate from device class. */
export function detectViewportLayout(width = typeof window !== 'undefined' ? window.innerWidth : 0) {
  if (width < 768) return 'narrow';
  if (width < 1024) return 'medium';
  return 'wide';
}

export function normalizeBrowser(ua = navigator.userAgent) {
  const agent = String(ua);
  if (/Edg\//i.test(agent) || /Edge/i.test(agent)) return 'Edge';
  if (/OPR\//i.test(agent) || /Opera/i.test(agent)) return 'Opera';
  if (/SamsungBrowser/i.test(agent)) return 'Samsung Internet';
  if (/CriOS/i.test(agent)) return 'Chrome';
  if (/FxiOS/i.test(agent)) return 'Firefox';
  if (/Chrome/i.test(agent)) return 'Chrome';
  if (/Firefox/i.test(agent)) return 'Firefox';
  if (/Safari/i.test(agent)) return 'Safari';
  return 'Unknown';
}

export function detectOS(ua = navigator.userAgent, platform = navigator.platform || '') {
  const agent = String(ua);
  if (/android/i.test(agent)) return 'Android';
  if (/iphone|ipod/i.test(agent)) return 'iOS';
  if (/ipad/i.test(agent)) return 'iOS';
  if (isTabletLike(agent) && /mac/i.test(platform)) return 'iOS';
  if (/win/i.test(platform) || /windows nt/i.test(agent)) return 'Windows';
  if (/mac/i.test(platform) || /mac os x/i.test(agent)) return 'macOS';
  if (/cros/i.test(agent)) return 'Chrome OS';
  if (/linux/i.test(platform) || /linux/i.test(agent)) return 'Linux';
  return 'Unknown';
}
